/**
 * Brand ownership of the host bar.
 *
 * The header "wears the brand that owns the bar":
 * - Dark-header platform themes (NeonFlux, Modern Dark, …) → navy host bar + endorsed
 *   EmpowerID parent lockup.
 * - Clean Light and dedicated product themes → single EmpowerID Identity Fabric lockup
 *   from themes.yaml branding (no triple stack).
 * - Customer white-label themes (Daimler, SAP, Aldi, …) → tenant header only; parent
 *   lockup suppressed so a white-label tenant never inherits platform chrome.
 *
 * Unknown/future customer themes default to white-label-safe behavior.
 */
export const EMPOWERNOW_OWNED_THEMES: ReadonlySet<string> = new Set([
  'blue', // NeonFlux (default dev theme)
  'white', // Clean Light (light header — not a navy host bar theme)
  'black', // Modern Dark
  'purple', // Purple Power
  'green', // Forest Green
  'orange', // Sunset Orange
]);

/** Subset with dark host bars that use the endorsed parent + platform lockup pattern. */
export const NAVY_HOST_BAR_THEMES: ReadonlySet<string> = new Set([
  'blue',
  'black',
  'purple',
  'green',
  'orange',
]);

export function isEmpowerNowOwnedTheme(theme: string | null | undefined): boolean {
  return !!theme && EMPOWERNOW_OWNED_THEMES.has(theme);
}

/**
 * Themes with a complete branding block in themes.yaml — own header lockup only;
 * never show the platform EmpowerID parent chrome or global logo fallbacks.
 */
export const DEDICATED_BRAND_THEMES: ReadonlySet<string> = new Set(['empowerid', 'europol']);

export function isDedicatedBrandTheme(theme: string | null | undefined): boolean {
  return !!theme && DEDICATED_BRAND_THEMES.has(theme);
}

/**
 * Product themes that use appName text in the header instead of a logo image.
 * Keeps NomadID (and similar) off legacy platform wordmarks without mixing
 * EmpowerID Identity Fabric chrome into a separate product line.
 */
export const TEXT_ONLY_HEADER_BRAND_THEMES: ReadonlySet<string> = new Set(['nomadid']);

export function isTextOnlyBrandTheme(theme: string | null | undefined): boolean {
  return !!theme && TEXT_ONLY_HEADER_BRAND_THEMES.has(theme);
}

/** Dark host bar + endorsed parent lockup (excludes Clean Light and dedicated product themes). */
export function usesEmpowerNowHostBar(theme: string | null | undefined): boolean {
  if (!theme || isDedicatedBrandTheme(theme)) return false;
  return NAVY_HOST_BAR_THEMES.has(theme);
}

/**
 * Customer / product themes whose header bar uses light text on a dark or
 * saturated background. BrandLogo `primary` should prefer logos.dark (light
 * wordmark) so the lockup stays visible on the host bar.
 */
export const LIGHT_TEXT_HEADER_THEMES: ReadonlySet<string> = new Set([
  'financial',
  'healthcare',
  'sap',
  'daimler',
  'continental',
  'aldi',
  'erste',
  'europol',
]);

export function usesLightTextHeader(theme: string | null | undefined): boolean {
  return !!theme && LIGHT_TEXT_HEADER_THEMES.has(theme);
}
